import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart } from '@fortawesome/free-solid-svg-icons'

import Content from './Content'
import parseJSON from 'src/utils/parseJSON'
import { sendPublic } from 'src/utils/messages/publicProtocol'

const POLL_INTERVAL = 30000

let changeRoot
const getChangeRoot = () => {
  if (changeRoot) return changeRoot

  changeRoot = document.getElementById('ChangeRoot')
  while (changeRoot.firstChild)
    changeRoot.removeChild(changeRoot.firstChild)
  return changeRoot
}

const App = ({ partner_item, partner_items, partner_key, widget, footer, fundraiser_url, donate_label }) => {
  const getTotals = data => {
    const { hybrid_raised_with_matching, total_donors, active_raised, active_donors } = data
    return {
      raised: widget.show_active_totals ? active_raised : hybrid_raised_with_matching,
      donors: widget.show_active_totals ? active_donors : total_donors,
    }
  }

  const [ partnerItem, setPartnerItem ] = useState(partner_item)
  const [ partnerItems, setPartnerItems ] = useState(partner_items)
  const [ donation, setDonation ] = useState(null)
  const [ expanded, setExpanded ] = useState(null)

  const toggleExpanded = section =>
    setExpanded(expanded => {
      switch (expanded) {
      case section: return null
      case null: return section
      default:
        setTimeout(() => setExpanded(section), 300)
        return null
      }
    })

  const sendMessage = (action, data = { itemId: partner_item.external_id }) =>
    sendPublic(parent, action, data)

  const update = async () => {
    const url = `/widgets/goal/${partner_key}/${partner_item.external_id}?with_children=1`
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (response.ok) {
      const { children, ...json } = await response.json()
      setPartnerItem(json)
      setPartnerItems(children)
    }
  }

  useEffect(() => {
    const interval = setInterval(update, POLL_INTERVAL)
    return () => clearInterval(interval)
  }, [ donation ])

  useEffect(() => {
    const handleDonated = data => {
      setDonation(data || {})
      update()
    }

    const handleMessage = e => {
      // FSA inbound. Not yet documented, not yet used by any partner,
      // so the wire name is free to change until we publish docs or
      // hand it to an integrator. Symmetric with our outbound
      // donationCompleted, which is the same payload coming the other
      // direction (host catches it from the donate iframe and forwards
      // here to refresh the goal display).
      if (e.data?.type === 'donationCompleted')
        return handleDonated(e.data.payload)

      // Legacy stringified `{ action: 'donated', data }`. Built for
      // Evite via jazzapp, which no longer emits it (confirmed grep
      // 2026-05-12), and never picked up by any other partner. Kept
      // as a paranoia hedge — remove on the next brave-mood pass.
      const data = parseJSON(e.data)
      if (data?.action === 'donated')
        return handleDonated(data.data)
    }

    addEventListener('message', handleMessage)
    return () => removeEventListener('message', handleMessage)
  }, [])

  return (
    <>
      <Content
        widget={widget}
        footer={footer}
        getTotals={getTotals}
        sendMessage={sendMessage}
        partnerItem={partnerItem}
        partnerItems={partnerItems}
        expanded={expanded}
        toggleExpanded={toggleExpanded}
        fundraiserUrl={fundraiser_url}
        donateLabel={donate_label}
      />
      {
        donation &&
        createPortal(
          <small className="text-success donation-thank-you-message">
            <FontAwesomeIcon icon={faHeart} className="me-2" />
            Thank you for your donation!
          </small>
        , getChangeRoot()
        ) ||
        widget.show_change_action &&
        createPortal(
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => sendMessage('changeBeneficiaryClicked')}
          >Change</button>
        , getChangeRoot()
        )
      }
    </>
  )
}

export default App
